<script setup>
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { computed } from 'vue'

import { useStatementUploadDestination } from '~/composables/useStatementUploadDestination'
import { statementCountLabel } from '~/libs/document-helper'
import { useBankConnectorStore } from '~/store/bank-connector'
import { useBankPullStatusUiStore } from '~/store/bank-pull-status-ui'

defineProps({
  message: {
    type: Object,
    default: () => ({}),
  },
})

// BR-12: no surface may render an "Upload statements" action when disableManualBankStatement is
// true - an unguarded CTA would reroute the module to LANDING instead of opening an upload.
const { destination, disableManualBankStatement, requestUpload } = useStatementUploadDestination()

const bankConnectorStore = useBankConnectorStore()
const bankPullStatusUiStore = useBankPullStatusUiStore()

// Names the bank(s) that actually failed, read from the same per-account classifier the popover
// uses (BR-9) - never re-derived independently.
const failedBankNames = computed(() => [...new Set(
  bankConnectorStore.normalizedCheckingAccounts
    .filter((account) => bankConnectorStore.getAccountPullState(account.accountId) === 'failed')
    .map((account) => account.bankName)
    .filter(Boolean)
)])

// Banks of the accounts still short of statements, for the counted copy.
const shortBankNames = computed(() => [...new Set(
  bankConnectorStore.normalizedCheckingAccounts
    .filter((account) => bankPullStatusUiStore.statementsNeededByAccountId[account.accountId] > 0)
    .map((account) => account.bankName)
    .filter(Boolean)
)])

const failureDetail = computed(() => {
  const total = bankPullStatusUiStore.statementsNeededTotal
  if (total > 0 && shortBankNames.value.length > 0) {
    const pronoun = total === 1 ? 'it' : 'them'
    return `We couldn't retrieve ${statementCountLabel(total)} from ${shortBankNames.value.join(' and ')}. Upload ${pronoun} to continue.`
  }
  return failedBankNames.value.length > 0
    ? `We were unable to retrieve statements from ${failedBankNames.value.join(' and ')}.`
    : 'We were unable to retrieve your bank statements.'
})
</script>

<template>
  <div class="tw:flex tw:flex-1 tw:flex-col tw:gap-2" data-cy="bank-pull-failure-toast">
    <div class="tw:flex tw:items-center tw:gap-2">
      <font-awesome-icon :icon="faXmark" class="red-300" />
      <span class="tw:type-500 tw:font-bold tw:text-w-neutral-900">Couldn't retrieve statements</span>
    </div>
    <p class="tw:type-400 tw:text-surface-700">
      {{ failureDetail }}
    </p>
    <a
      v-if="!disableManualBankStatement"
      :href="destination"
      class="tw:type-500 tw:self-start tw:text-blue-400 tw:underline"
      data-cy="bank-pull-failure-toast-upload-link"
      @click.prevent="requestUpload"
    >
      Upload statements
    </a>
  </div>
</template>
